import { useState, useEffect, useRef } from 'react';
import { Sun, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/use-debounce';
import { usePipelineContext } from '../../context';
import { PipelineToolbar } from './PipelineToolbar';
import { PipelinePresence } from './PipelinePresence';
import { ArchiveSidebar } from './ArchiveSidebar';
import { UnassignedEnquiryQueue } from './UnassignedEnquiryQueue';

/**
 * Pipeline header with title, search, and controls
 * Responsive layout for mobile and desktop
 *
 * Owns the search input state locally and debounces before pushing to context,
 * so keystrokes don't cause every context consumer to re-render.
 */
export function PipelineHeader() {
  const { state, actions, meta } = usePipelineContext();
  const { otherActiveUsers, totalMatches } = meta;
  const searchActive = state.searchQuery.length > 0;

  // Local input state — only this component re-renders on each keystroke
  const [localInput, setLocalInput] = useState('');
  const localInputRef = useRef(localInput);
  localInputRef.current = localInput;
  const debouncedInput = useDebounce(localInput, 150);

  // Push debounced value to context (triggers filtering)
  useEffect(() => {
    actions.setSearchQuery(debouncedInput);
  }, [debouncedInput, actions]);

  // Sync external clear (e.g. "Clear search" button in GlobalSearchResultsPanel)
  useEffect(() => {
    if (state.searchQuery === '' && localInputRef.current !== '') {
      setLocalInput('');
    }
  }, [state.searchQuery]);

  return (
    <div className="border-b border-input bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 py-4">
        {/* Mobile-first responsive header */}
        <div className="space-y-4">
          {/* Title and Description */}
          <div className="flex items-center gap-3">
            <div className="accent-icon-bg">
              <Sun className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
                Sales Pipeline
              </h1>
              <p className="text-sm text-muted-foreground hidden sm:block">
                Manage your solar sales pipeline and track customer progress
              </p>
            </div>
          </div>

          {/* Controls - Responsive layout */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search - Full width on mobile */}
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search leads..."
                className="pl-9 pr-9 w-full sm:w-64"
                value={localInput}
                onChange={(e) => setLocalInput(e.target.value)}
              />
              {localInput && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1">
                  {searchActive && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {totalMatches}
                    </span>
                  )}
                  <button
                    type="button"
                    className="p-1.5 rounded-full hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLocalInput('');
                      actions.setSearchQuery('');
                    }}
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
              )}
            </div>

            {/* Toolbar buttons */}
            <PipelineToolbar />
            <UnassignedEnquiryQueue />
            <ArchiveSidebar />
          </div>

          {/* Active users presence indicator */}
          <PipelinePresence users={otherActiveUsers} />
        </div>
      </div>
    </div>
  );
}
